import { z } from "zod";
import { Timestamp } from "firebase/firestore";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const firestoreTimestamp = z.instanceof(Timestamp, {
  message: "Must be a Firestore Timestamp",
});

// ---------------------------------------------------------------------------
// Crew member (embedded in Game)
// ---------------------------------------------------------------------------

const crewMemberSchema = z.object({
  // Empty string allowed for the signed-in user slot (isUser: true)
  personId: z.string(),
  name: z.string().min(1),
  position: z.string().min(1),
  isUser: z.boolean(),
  phone: z.string().optional(),
});

// ---------------------------------------------------------------------------
// Pay info (embedded in Game)
// ---------------------------------------------------------------------------

const payInfoSchema = z.object({
  promisedAmount: z.number().min(0),
  paidAmount: z.number().min(0).optional(),
  status: z.enum(["unpaid", "partial", "paid"]),
  paidDate: firestoreTimestamp.optional(),
  estimatedPaymentDate: firestoreTimestamp.optional(),
  method: z.string().optional(),
  notes: z.string().optional(),
});

// ---------------------------------------------------------------------------
// Entity create schemas
// ---------------------------------------------------------------------------

export const gameCreateSchema = z.object({
  dateTime: firestoreTimestamp,
  sport: z.string().min(1, "Sport is required"),
  organizationId: z.string().min(1, "Organization is required"),
  locationName: z.string().min(1, "Location name is required"),
  crew: z.array(crewMemberSchema).min(1, "At least one crew member is required"),
  pay: payInfoSchema,
  horizonGameId: z.string().optional(),
});

export const personCreateSchema = z.object({
  name: z.string().min(1, "Name is required"),
  primaryRole: z.enum([
    "official",
    "assignor",
    "administrator",
    "coach",
    "scorekeeper",
    "contact",
    "other",
  ]),
});

export const organizationCreateSchema = z.object({
  name: z.string().min(1, "Name is required"),
  sport: z.string().min(1, "Sport is required"),
});

export const expenseCreateSchema = z.object({
  date: firestoreTimestamp,
  vendor: z.string().min(1, "Vendor is required"),
  amount: z.number().positive("Amount must be positive"),
  description: z.string().default(""),
  notes: z.string().optional(),
  category: z.enum([
    "equipment_supplies",
    "travel_meals",
    "uniforms_gear",
    "training_certification",
    "dues_memberships",
    "communication",
    "office_supplies",
    "insurance",
    "other",
  ]),
  taxDeductible: z.boolean().default(true),
  deductiblePercentage: z.number().min(0).max(100).default(100),
  autoLinked: z.boolean().default(false),
  taxYear: z.string().min(1, "Tax year is required"),
  source: z.enum(["manual", "receipt_scan", "imported", "voice"]).default("manual"),
  receiptUrl: z.string().url().optional(),
  receiptParsedAt: firestoreTimestamp.optional(),
  receiptParseConfidence: z.enum(["high", "medium", "low"]).optional(),
  gameIds: z.array(z.string()).optional(),
});

export const locationCreateSchema = z.object({
  name: z.string().min(1, "Name is required"),
});

// ---------------------------------------------------------------------------
// Validation helper
// ---------------------------------------------------------------------------

export function validate<T>(schema: z.ZodSchema<T>, data: unknown): T {
  const result = schema.safeParse(data);
  if (!result.success) {
    const messages = result.error.issues
      .map((i) => `${i.path.join(".")}: ${i.message}`)
      .join("; ");
    throw new Error(`Validation failed: ${messages}`);
  }
  return result.data;
}
